import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./FinancePage.module.css";
import { DashboardWithTabs } from "./components/dashboard/DashboardWithTabs";
import type { FinanceTabId } from "./components/dashboard/FinanceTabs";
import { useFinanceStore } from "./store/financeStore";
import MetaCallbackPage from "../MetaCallbackPage";
import { OnboardingFlow } from "./components/onboarding/OnboardingFlow";

const readActiveTab = (): FinanceTabId => {
  if (typeof window === "undefined") return "company";
  const storedTab = sessionStorage.getItem("finance_active_tab");
  return storedTab === "company" ||
    storedTab === "funnels" ||
    storedTab === "logistics" ||
    storedTab === "ads" ||
    storedTab === "reports"
    ? storedTab
    : "company";
};

const shouldForceFinanceOnboarding = (searchParams: URLSearchParams) => {
  return searchParams.get("onboarding") === "finance";
};

const readInitialOnboardingStep = (searchParams: URLSearchParams): 1 | 2 | 3 | 4 | 5 => {
  return searchParams.get("step") === "fixed-costs" ? 4 : 1;
};

const FinancePage = () => {
  const navigate = useNavigate();
  const fetchStatus = useFinanceStore((state) => state.fetchStatus);
  const onboardingCompleted = useFinanceStore((state) => state.onboardingCompleted);
  const statusLoading = useFinanceStore((state) => state.statusLoading);
  const statusError = useFinanceStore((state) => state.statusError);
  const [activeTab, setActiveTab] = useState<FinanceTabId>(() => readActiveTab());
  const [searchParams] = useSearchParams();
  const [forceOnboarding, setForceOnboarding] = useState(() => shouldForceFinanceOnboarding(searchParams));
  const initialOnboardingStep = readInitialOnboardingStep(searchParams);
  const finishAfterFixedCosts = searchParams.get("step") === "fixed-costs";
  const callbackStatus = searchParams.get("status");

  const isMetaCallback =
    callbackStatus === "connected" || callbackStatus === "error" || callbackStatus === "cancelled";

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    setForceOnboarding(shouldForceFinanceOnboarding(searchParams));
  }, [searchParams]);

  if (isMetaCallback) {
    return <MetaCallbackPage />;
  }

  if (statusLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        Cargando Fluxi Finance...
      </div>
    );
  }

  if (statusError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: "16px",
        }}
      >
        <p style={{ color: "#ef4444", fontSize: "14px" }}>{statusError}</p>
        <button
          onClick={() => fetchStatus()}
          style={{
            padding: "8px 16px",
            borderRadius: "999px",
            border: "1px solid #2b5ce6",
            background: "white",
            color: "#2b5ce6",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (forceOnboarding || !onboardingCompleted) {
    return (
      <div className={`${styles.financePage} ${styles.financeOnboardingPage}`}>
        <OnboardingFlow
          key={`${initialOnboardingStep}-${finishAfterFixedCosts ? "fixed-costs" : "full"}`}
          initialStep={initialOnboardingStep}
          finishAfterFixedCosts={finishAfterFixedCosts}
          onFinished={() => {
            setForceOnboarding(false);
            setActiveTab("company");
            navigate("/finance", { replace: true });
          }}
        />
      </div>
    );
  }

  return (
    <div className={`${styles.financePage} ${styles.financeDashboardPage}`}>
      <DashboardWithTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default FinancePage;
